// Auctions page: one grid of live lots. Live bid, bid count and end time come from the Auction+
// app proxy (the same /auction call its product-page widget makes; bidding stays in the widget).
// Sort: Ending soon (default, by Auction+ end time) / Newest / Lowest bid — within the page.
// Category chips filter; ?cat=books or ?cat=cards,packs preselects, ?sort=newest|lowest too.
(() => {
  const pad = (n) => String(n).padStart(2, '0');
  const left = (ms) => {
    const s = Math.max(0, Math.floor(ms / 1000));
    const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${pad(m)}m`;
    return `${m}m ${pad(s % 60)}s`;
  };
  const SORTS = ['ending', 'newest', 'lowest'];

  const init = (root) => {
    if (root.dataset.dtBound) return;
    root.dataset.dtBound = '1';
    const ds = root.dataset;
    const grid = root.querySelector('[data-dt-lot-grid]');
    if (!grid) return;
    const money = new Intl.NumberFormat(ds.locale || undefined, { style: 'currency', currency: ds.currency || 'USD' });
    const plural = new Intl.PluralRules(ds.locale || undefined);
    const lots = new Map(); // gid -> { li, card, end, live, bid, created }
    grid.querySelectorAll(':scope > li').forEach((li) => {
      const card = li.querySelector('[data-dt-lot]');
      lots.set(card.dataset.dtLot, {
        li, card, end: NaN, live: true, pending: li.hasAttribute('data-dt-pending'),
        bid: parseFloat(li.dataset.bid), created: parseInt(li.dataset.created, 10) || 0
      });
    });
    const params = new URLSearchParams(location.search);

    const tick = () => {
      const now = Date.now();
      lots.forEach((lot) => {
        if (!Number.isFinite(lot.end)) return;
        const ms = lot.end - now;
        const el = lot.card.querySelector('[data-dt-lot-timer]');
        el.hidden = false;
        el.textContent = ms > 0 && lot.live ? ds.labelEnds.replace('__TIME__', left(ms)) : ds.labelEnded;
        el.classList.toggle('is-urgent', ms > 0 && ms < 3600000);
      });
    };

    // Chips
    const known = [...root.querySelectorAll('[data-dt-lot-chips] .dt-chip')].map((c) => c.dataset.chip).filter(Boolean);
    let chips = new Set((params.get('cat') || '').split(',').map((c) => c.trim()).filter((c) => known.includes(c)));
    const syncChipButtons = () => {
      root.querySelectorAll('[data-dt-lot-chips] .dt-chip').forEach((c) => {
        const on = c.dataset.chip === '' ? chips.size === 0 : chips.has(c.dataset.chip);
        c.classList.toggle('is-active', on);
        c.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    };
    const applyChips = () => {
      let shown = 0;
      lots.forEach((lot) => {
        // Pending lots (not flagged live by Auction+) stay hidden until the proxy confirms a live auction.
        const on = !lot.pending && (chips.size === 0 || chips.has(lot.card.dataset.chip));
        lot.li.hidden = !on;
        if (on) shown += 1;
      });
      const none = root.querySelector('[data-dt-lot-nomatch]');
      if (none) none.hidden = shown > 0;
      // A lane section (Reseller Lots on the hub) shows only while it has a lot to show.
      const lane = root.closest('[data-dt-lane]');
      if (lane) lane.hidden = shown === 0;
    };

    // Sort
    let sort = SORTS.includes(params.get('sort')) ? params.get('sort') : 'ending';
    const syncSortButtons = () => {
      root.querySelectorAll('[data-dt-lot-sort] .dt-sort').forEach((b) => {
        const on = b.dataset.sort === sort;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    };
    const applySort = () => {
      const key = {
        ending: (l) => (l.live && Number.isFinite(l.end) ? l.end : Infinity),
        newest: (l) => -l.created,
        lowest: (l) => (Number.isFinite(l.bid) ? l.bid : Infinity)
      }[sort];
      // Stable: ties keep the server's newest-first order.
      const order = [...lots.values()].map((l, i) => ({ l, i, k: key(l) }));
      order.sort((a, b) => (a.k - b.k) || (a.i - b.i));
      order.forEach(({ l }) => grid.appendChild(l.li));
    };

    const setParam = (name, value) => {
      try {
        const url = new URL(location.href);
        if (value) url.searchParams.set(name, value);
        else url.searchParams.delete(name);
        history.replaceState(history.state, '', url);
      } catch (e) {}
    };

    const load = async (id, lot) => {
      const url = `${ds.proxy}/auction?product_id=${encodeURIComponent(id)}&shop=${encodeURIComponent(ds.shop)}`;
      try {
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!res.ok) return;
        const json = await res.json();
        const data = json && json.data ? json.data : null;
        if (!json || json.success === false || !data || !data.id) return;
        lot.live = !data.status || data.status === 'ACTIVE';
        if (lot.pending && lot.live) lot.pending = false;
        lot.end = data.endTime ? new Date(data.endTime).getTime() : NaN;
        const amount = parseFloat(data.currentPrice ?? data.startingBid);
        if (Number.isFinite(amount)) {
          lot.bid = amount;
          lot.card.querySelector('[data-dt-lot-bid]').textContent = money.format(amount);
          const wait = lot.card.querySelector('[data-dt-lot-bid-wait]');
          if (wait) wait.removeAttribute('data-dt-lot-bid-wait');
        }
        const hideCount = !!(data.widgetSettings && data.widgetSettings.hideBidCount);
        const count = parseInt(data.bidCount, 10);
        const el = lot.card.querySelector('[data-dt-lot-count]');
        if (!hideCount && Number.isFinite(count)) {
          const label = plural.select(count) === 'one' ? ds.labelBidsOne : ds.labelBidsOther;
          el.textContent = label.replace('__N__', count);
          el.hidden = false;
        }
        lot.card.classList.toggle('is-ended', !lot.live);
      } catch (e) {}
    };

    // A handful at a time: one proxy call per lot, and a full page is 48.
    const queue = [...lots.entries()];
    const worker = async () => {
      while (queue.length) {
        const [id, lot] = queue.shift();
        await load(id, lot);
      }
    };
    Promise.all(Array.from({ length: Math.min(6, queue.length) }, worker)).then(() => {
      applyChips();
      applySort();
      tick();
    });

    root.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-dt-lot-chips] .dt-chip');
      if (chip) {
        chips = new Set(chip.dataset.chip ? [chip.dataset.chip] : []);
        syncChipButtons();
        applyChips();
        setParam('cat', [...chips].join(','));
        return;
      }
      const btn = event.target.closest('[data-dt-lot-sort] .dt-sort');
      if (btn) {
        sort = btn.dataset.sort;
        syncSortButtons();
        applySort();
        setParam('sort', sort === 'ending' ? '' : sort);
      }
    });

    syncChipButtons();
    syncSortButtons();
    applyChips();
    applySort();
    setInterval(tick, 1000);
  };

  const boot = (scope) => scope.querySelectorAll('[data-dt-auctions]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(document));
  else boot(document);
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
